import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, Minus, ThumbsDown, MessageSquare, Send, ArrowLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, differenceInCalendarDays } from "date-fns";

function formatRelativeReply(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = differenceInCalendarDays(now, d);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return format(d, sameYear ? "MMM d" : "MMM d, yyyy");
}
import { sendInboxReply } from "@/lib/inboxHelpers";
import { toast } from "@/hooks/use-toast";

interface ResultsIntentCardProps {
  campaignId: string;
}

type IntentType = "positive" | "neutral" | "negative";

export function ResultsIntentCard({ campaignId }: ResultsIntentCardProps) {
  const [targetListIntent, setTargetListIntent] = useState<IntentType | null>(null);
  const [previousIntent, setPreviousIntent] = useState<IntentType | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch replied targets with intent
  const { data: targets = [] } = useQuery({
    queryKey: ["campaign-intent-targets", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("targets")
        .select("id, name, first_name, last_name, company, title, reply_intent")
        .eq("campaign_id", campaignId)
        .eq("status", "replied")
        .not("reply_intent", "is", null);
      if (error) throw error;
      return data || [];
    },
    enabled: !!campaignId,
  });

  // Fetch campaign's client_id for sending replies
  const { data: campaign } = useQuery({
    queryKey: ["campaign-client-id", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("client_id")
        .eq("id", campaignId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!campaignId,
  });

  // Fetch profile pictures and latest reply timestamp per target
  const targetIds = useMemo(() => targets.map((t) => t.id), [targets]);
  const { data: replyMeta = { picMap: new Map<string, string>(), lastReplyMap: new Map<string, string>() } } = useQuery({
    queryKey: ["target-reply-meta", targetIds],
    queryFn: async () => {
      const picMap = new Map<string, string>();
      const lastReplyMap = new Map<string, string>();
      if (targetIds.length === 0) return { picMap, lastReplyMap };
      const { data, error } = await supabase
        .from("client_messages")
        .select("target_id, sender_picture_url, received_at")
        .in("target_id", targetIds)
        .eq("is_from_me", false)
        .order("received_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      (data || []).forEach((row) => {
        if (!row.target_id) return;
        if (row.sender_picture_url && !picMap.has(row.target_id)) {
          picMap.set(row.target_id, row.sender_picture_url);
        }
        if (row.received_at && !lastReplyMap.has(row.target_id)) {
          lastReplyMap.set(row.target_id, row.received_at);
        }
      });
      return { picMap, lastReplyMap };
    },
    enabled: targetIds.length > 0,
  });
  const profilePicMap = replyMeta.picMap;
  const lastReplyMap = replyMeta.lastReplyMap;

  // Fetch conversation for selected target
  const { data: messages = [] } = useQuery({
    queryKey: ["target-conversation", selectedTargetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_messages")
        .select("id, message_text, is_from_me, received_at, sender_picture_url, sender_name")
        .eq("target_id", selectedTargetId!)
        .order("received_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedTargetId,
  });

  // Auto-scroll to bottom when messages load or change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const grouped = useMemo(() => {
    const result: Record<IntentType, typeof targets> = {
      positive: [],
      neutral: [],
      negative: [],
    };
    targets.forEach((t) => {
      const intent = t.reply_intent as IntentType;
      if (result[intent]) result[intent].push(t);
    });
    // Sort each bucket by latest reply (most recent first)
    (Object.keys(result) as IntentType[]).forEach((k) => {
      result[k] = [...result[k]].sort((a, b) => {
        const ta = lastReplyMap.get(a.id);
        const tb = lastReplyMap.get(b.id);
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return new Date(tb).getTime() - new Date(ta).getTime();
      });
    });
    return result;
  }, [targets, lastReplyMap]);

  const selectedTarget = useMemo(
    () => targets.find((t) => t.id === selectedTargetId),
    [targets, selectedTargetId]
  );

  const targetAvatar = useMemo(() => {
    const incoming = messages.find((m) => !m.is_from_me);
    return incoming?.sender_picture_url || null;
  }, [messages]);

  const getInitials = (target: { first_name?: string | null; last_name?: string | null; name?: string | null }) => {
    if (target.first_name && target.last_name) return `${target.first_name[0]}${target.last_name[0]}`.toUpperCase();
    return (target.name || "?").slice(0, 2).toUpperCase();
  };

  const getTargetDisplayName = (target: { first_name?: string | null; last_name?: string | null; name?: string | null }) => {
    if (target.first_name && target.last_name) return `${target.first_name} ${target.last_name}`;
    return target.name || "Unknown";
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !campaign?.client_id || messages.length === 0) return;
    const lastMessage = messages[messages.length - 1];
    setIsSending(true);
    try {
      await sendInboxReply(campaign.client_id, lastMessage.id, replyText.trim());
      setReplyText("");
      toast({ title: "Reply sent", description: "Your message has been sent." });
      queryClient.invalidateQueries({ queryKey: ["target-conversation", selectedTargetId] });
    } catch (err: any) {
      toast({ title: "Failed to send", description: err?.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  const intentConfig: Record<IntentType, { label: string; icon: typeof ThumbsUp; colorClass: string; badgeVariant: string }> = {
    positive: { label: "Positive", icon: ThumbsUp, colorClass: "text-green-600 dark:text-green-400", badgeVariant: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60" },
    neutral: { label: "Neutral", icon: Minus, colorClass: "text-muted-foreground", badgeVariant: "bg-muted text-muted-foreground hover:bg-muted/80" },
    negative: { label: "Negative", icon: ThumbsDown, colorClass: "text-red-600 dark:text-red-400", badgeVariant: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60" },
  };

  if (targets.length === 0) return null;

  return (
    <>
      <Card className="shadow-sm border-sidebar-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap">
            {(["positive", "neutral", "negative"] as IntentType[]).map((intent) => {
              const config = intentConfig[intent];
              const Icon = config.icon;
              const count = grouped[intent].length;
              return (
                <button
                  key={intent}
                  onClick={() => count > 0 && setTargetListIntent(intent)}
                  disabled={count === 0}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all text-sm font-medium",
                    count > 0
                      ? "border-border hover:border-primary/40 cursor-pointer"
                      : "border-border/50 opacity-50 cursor-not-allowed"
                  )}
                >
                  <Icon className={cn("h-4 w-4", config.colorClass)} />
                  <span>{config.label}</span>
                  <span className={cn("px-1.5 py-0.5 rounded-full text-xs font-semibold", config.badgeVariant)}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog 1: Target List */}
      <Dialog open={!!targetListIntent} onOpenChange={(open) => !open && setTargetListIntent(null)}>
        <DialogContent className="max-w-md flex flex-col" style={{ maxHeight: '70vh' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {targetListIntent && (
                <>
                  {(() => { const Icon = intentConfig[targetListIntent].icon; return <Icon className={cn("h-5 w-5", intentConfig[targetListIntent].colorClass)} />; })()}
                  <span>{intentConfig[targetListIntent].label} Replies</span>
                  <Badge variant="outline" className="ml-auto">{grouped[targetListIntent]?.length || 0}</Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-2">
            <div className="space-y-1 px-2">
              {targetListIntent && grouped[targetListIntent].map((target) => (
                <div
                  key={target.id}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/60 transition-colors"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={profilePicMap.get(target.id) || undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {getInitials(target)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{getTargetDisplayName(target)}</p>
                    {(target.title || target.company) && (
                      <p className="text-xs text-muted-foreground truncate">
                        {[target.title, target.company].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {lastReplyMap.get(target.id) && (
                      <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                        Replied {formatRelativeReply(lastReplyMap.get(target.id))}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-xs h-7 gap-1"
                    onClick={() => {
                      setPreviousIntent(targetListIntent);
                      setSelectedTargetId(target.id);
                      setTargetListIntent(null);
                    }}
                  >
                    <MessageSquare className="h-3 w-3" />
                    View Message
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog 2: Conversation + Reply */}
      <Dialog open={!!selectedTargetId} onOpenChange={(open) => !open && setSelectedTargetId(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  setSelectedTargetId(null);
                  setReplyText("");
                  if (previousIntent) {
                    setTargetListIntent(previousIntent);
                    setPreviousIntent(null);
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Avatar className="h-8 w-8">
                <AvatarImage src={profilePicMap.get(selectedTargetId!) || targetAvatar || undefined} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {selectedTarget ? getInitials(selectedTarget) : "?"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">
                  {selectedTarget ? getTargetDisplayName(selectedTarget) : ""}
                </p>
                {selectedTarget && (selectedTarget.title || selectedTarget.company) && (
                  <p className="text-xs text-muted-foreground font-normal truncate">
                    {[selectedTarget.title, selectedTarget.company].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              {selectedTarget?.reply_intent && (
                <Badge variant="outline" className={cn("ml-auto text-xs shrink-0", intentConfig[selectedTarget.reply_intent as IntentType]?.badgeVariant)}>
                  {intentConfig[selectedTarget.reply_intent as IntentType]?.label}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Messages */}
          <ScrollArea className="flex-1 min-h-0 pr-2" ref={scrollRef}>
            <div className="space-y-3 py-2">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "flex flex-col max-w-[85%]",
                    msg.is_from_me ? "ml-auto items-end" : "mr-auto items-start"
                  )}
                >
                  <div
                    className={cn(
                      "px-3 py-2 rounded-xl text-sm",
                      msg.is_from_me
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-muted rounded-bl-sm"
                    )}
                  >
                    {msg.message_text}
                  </div>
                  <span className="text-[10px] text-muted-foreground mt-1 px-1">
                    {msg.received_at ? format(new Date(msg.received_at), "MMM d, h:mm a") : ""}
                  </span>
                </div>
              ))}
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No messages found.</p>
              )}
            </div>
          </ScrollArea>

          {/* Reply Bar */}
          <div className="border-t pt-3 flex gap-2 items-end">
            <Textarea
              placeholder="Type a reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              className="min-h-[40px] max-h-[100px] resize-none text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
            />
            <Button
              size="icon"
              onClick={handleSendReply}
              disabled={!replyText.trim() || isSending || messages.length === 0}
              className="shrink-0"
            >
              {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
